import React, { useState, useEffect, useCallback } from 'react';
import { Plus, ChevronDown, ChevronUp, Pencil, Trash2, Link2, TrendingUp } from 'lucide-react';
import { hasPI } from '../../../lib/permissions';
import { piApi } from '../../../services/piApi';
import '../PIShell.css';

const STATUS_CONFIG = {
  identified:  { label: 'Identified',  cls: 'pi-badge-blue'  },
  in_progress: { label: 'In Progress', cls: 'pi-badge-amber' },
  realized:    { label: 'Realized',    cls: 'pi-badge-green' },
  missed:      { label: 'Missed',      cls: 'pi-badge-red'   },
};

const labelStyle = { fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 };
const inputStyle = { width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };

function fmt(v) { return v != null ? `$${Number(v).toFixed(1)}M` : '—'; }

function computePreview(gross, confPct, months) {
  const g = parseFloat(gross) || 0;
  const c = parseFloat(confPct) || 0;
  const m = parseInt(months) || 0;
  if (!g || !c) return null;
  const riskAdj = g * (c / 100);
  const annualized = m > 0 ? riskAdj * (12 / m) : riskAdj;
  return { riskAdj: Math.round(riskAdj * 100) / 100, annualized: Math.round(annualized * 100) / 100 };
}

function LinkedPortcoTag({ name, role }) {
  if (!name) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '2px 6px', background: '#ede9fe', color: '#6d28d9', borderRadius: 4 }}>
      <Link2 size={9} /> {role === 'addon' ? 'Add-on' : 'Platform'}: {name}
    </span>
  );
}

function SynergyCard({ synergy, canEdit, onUpdate, linkedPortcos }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const rag = STATUS_CONFIG[synergy.status] || STATUS_CONFIG.identified;
  const progressPct = synergy.modeled_value
    ? Math.min(100, Math.round(((synergy.realized_value || 0) / synergy.modeled_value) * 100))
    : 0;

  const linked = linkedPortcos.find(p => p.id === synergy.linked_portco_id);

  const startEdit = () => {
    setForm({
      title: synergy.title || '',
      status: synergy.status || 'identified',
      linked_portco_id: synergy.linked_portco_id || '',
      gross_opportunity: synergy.gross_opportunity ?? '',
      confidence_pct: synergy.confidence_pct ?? '',
      realization_months: synergy.realization_months ?? '',
      realized_value: synergy.realized_value ?? '',
      target_date: synergy.target_date ? synergy.target_date.slice(0, 10) : '',
      realization_date: synergy.realization_date ? synergy.realization_date.slice(0, 10) : '',
      notes: synergy.notes || '',
    });
    setEditing(true);
    setExpanded(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await piApi.updateSynergy(synergy.portco_id, synergy.id, {
        ...form,
        gross_opportunity: form.gross_opportunity !== '' ? parseFloat(form.gross_opportunity) : null,
        confidence_pct: form.confidence_pct !== '' ? parseFloat(form.confidence_pct) : null,
        realization_months: form.realization_months !== '' ? parseInt(form.realization_months) : null,
        realized_value: form.realized_value !== '' ? parseFloat(form.realized_value) : null,
        linked_portco_id: form.linked_portco_id || null,
        target_date: form.target_date || null,
        realization_date: form.realization_date || null,
      });
      setEditing(false);
      onUpdate();
    } catch (e) { console.error('[Synergy] Update error:', e); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this synergy? This will also remove its EBITDA bridge item.')) return;
    try {
      await piApi.deleteSynergy(synergy.portco_id, synergy.id);
      onUpdate();
    } catch (e) { console.error('[Synergy] Delete error:', e); }
  };

  const preview = editing ? computePreview(form.gross_opportunity, form.confidence_pct, form.realization_months) : null;

  return (
    <div className="pi-card" style={{ marginBottom: 10 }}>
      <div style={{ padding: '12px 14px', cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 13.5, color: '#1e293b' }}>{synergy.title}</span>
            <span style={{ color: '#94a3b8' }}>{expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
            {linked && <LinkedPortcoTag name={linked.name} role={linked.role} />}
          </div>
          <span className={`pi-badge ${rag.cls}`}>{rag.label}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <div style={{ flex: 1, height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${progressPct}%`, height: '100%', borderRadius: 3, background: '#4f46e5' }} />
          </div>
          <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
            {fmt(synergy.realized_value || 0)} / {fmt(synergy.risk_adjusted_value || synergy.modeled_value || 0)}
          </span>
        </div>
        {(synergy.annualized_run_rate || synergy.confidence_pct) && (
          <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#64748b' }}>
            {synergy.confidence_pct && <span>Confidence: {synergy.confidence_pct}%</span>}
            {synergy.realization_months && <span>Timeline: {synergy.realization_months}mo</span>}
            {synergy.annualized_run_rate && <span style={{ color: '#4f46e5', fontWeight: 600 }}>Run-rate: {fmt(synergy.annualized_run_rate)}/yr</span>}
          </div>
        )}
      </div>
      {expanded && (
        <div style={{ padding: '12px 14px', borderTop: '1px solid #f1f5f9' }}>
          {!editing ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <span style={labelStyle}>GROSS OPPORTUNITY</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{fmt(synergy.gross_opportunity)}</span>
                </div>
                <div>
                  <span style={labelStyle}>CONFIDENCE</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{synergy.confidence_pct != null ? `${synergy.confidence_pct}%` : '—'}</span>
                </div>
                <div>
                  <span style={labelStyle}>REALIZATION PERIOD</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{synergy.realization_months ? `${synergy.realization_months} months` : '—'}</span>
                </div>
                <div>
                  <span style={labelStyle}>RISK-ADJUSTED VALUE</span>
                  <span style={{ fontSize: 13, color: '#4f46e5', fontWeight: 600 }}>{fmt(synergy.risk_adjusted_value)}</span>
                </div>
                <div>
                  <span style={labelStyle}>ANNUALIZED RUN-RATE</span>
                  <span style={{ fontSize: 13, color: '#4f46e5', fontWeight: 600 }}>{synergy.annualized_run_rate ? `${fmt(synergy.annualized_run_rate)}/yr` : '—'}</span>
                </div>
                <div>
                  <span style={labelStyle}>REALIZED VALUE</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{fmt(synergy.realized_value)}</span>
                </div>
                <div>
                  <span style={labelStyle}>TARGET DATE</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{synergy.target_date ? new Date(synergy.target_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                </div>
                <div>
                  <span style={labelStyle}>REALIZATION DATE</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{synergy.realization_date ? new Date(synergy.realization_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                </div>
                <div>
                  <span style={labelStyle}>PROGRESS</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{progressPct}%</span>
                </div>
              </div>
              {synergy.notes && (
                <div style={{ marginBottom: 12 }}>
                  <span style={labelStyle}>NOTES</span>
                  <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.45, margin: 0 }}>{synergy.notes}</p>
                </div>
              )}
              {canEdit && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="pi-btn-ghost" onClick={(e) => { e.stopPropagation(); handleDelete(); }} style={{ color: '#dc2626', fontSize: 12 }}>
                    <Trash2 size={12} style={{ marginRight: 4 }} /> Delete
                  </button>
                  <button className="pi-btn-primary" onClick={(e) => { e.stopPropagation(); startEdit(); }} style={{ fontSize: 12 }}>
                    <Pencil size={12} style={{ marginRight: 4 }} /> Edit
                  </button>
                </div>
              )}
            </>
          ) : (
            <div onClick={e => e.stopPropagation()}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>TITLE *</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>STATUS</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>LINKED ADD-ON</label>
                  <select value={form.linked_portco_id} onChange={e => setForm(f => ({ ...f, linked_portco_id: e.target.value }))} style={inputStyle}>
                    <option value="">— None —</option>
                    {linkedPortcos.map(p => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>GROSS OPPORTUNITY ($M) *</label>
                  <input type="number" step="0.1" value={form.gross_opportunity} onChange={e => setForm(f => ({ ...f, gross_opportunity: e.target.value }))} placeholder="e.g. 5.0" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>CONFIDENCE (%) *</label>
                  <input type="number" min="0" max="100" step="5" value={form.confidence_pct} onChange={e => setForm(f => ({ ...f, confidence_pct: e.target.value }))} placeholder="e.g. 75" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>REALIZATION PERIOD (months)</label>
                  <input type="number" min="1" max="120" value={form.realization_months} onChange={e => setForm(f => ({ ...f, realization_months: e.target.value }))} placeholder="e.g. 18" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>REALIZED VALUE ($M)</label>
                  <input type="number" step="0.1" value={form.realized_value} onChange={e => setForm(f => ({ ...f, realized_value: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>TARGET DATE</label>
                  <input type="date" value={form.target_date} onChange={e => setForm(f => ({ ...f, target_date: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>REALIZATION DATE</label>
                  <input type="date" value={form.realization_date} onChange={e => setForm(f => ({ ...f, realization_date: e.target.value }))} style={inputStyle} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>NOTES</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
              </div>
              {/* Live preview of computed values */}
              {preview && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', marginBottom: 10, display: 'flex', gap: 16, fontSize: 12 }}>
                  <TrendingUp size={14} color="#4f46e5" />
                  <span>Risk-Adjusted: <strong style={{ color: '#4f46e5' }}>{fmt(preview.riskAdj)}</strong></span>
                  <span>Annualized Run-Rate: <strong style={{ color: '#4f46e5' }}>{fmt(preview.annualized)}/yr</strong></span>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="pi-btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
                <button className="pi-btn-primary" onClick={handleSave} disabled={saving || !form.title}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddSynergyForm({ portcoId, type, linkedPortcos, onSaved, onCancel }) {
  const [form, setForm] = useState({
    title: '', status: 'identified', linked_portco_id: '',
    gross_opportunity: '', confidence_pct: '', realization_months: '',
    realized_value: '', target_date: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const preview = computePreview(form.gross_opportunity, form.confidence_pct, form.realization_months);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await piApi.createSynergy(portcoId, {
        ...form, type,
        gross_opportunity: form.gross_opportunity !== '' ? parseFloat(form.gross_opportunity) : null,
        confidence_pct: form.confidence_pct !== '' ? parseFloat(form.confidence_pct) : null,
        realization_months: form.realization_months !== '' ? parseInt(form.realization_months) : null,
        realized_value: form.realized_value !== '' ? parseFloat(form.realized_value) : null,
        linked_portco_id: form.linked_portco_id || null,
        target_date: form.target_date || null,
      });
      onSaved();
    } catch (e) { console.error('[Synergy] Create error:', e); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="pi-card" style={{ padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>TITLE *</label>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="e.g. Cross-sell platform products to add-on customer base" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>LINKED ADD-ON</label>
          <select value={form.linked_portco_id} onChange={e => setForm(f => ({ ...f, linked_portco_id: e.target.value }))} style={inputStyle}>
            <option value="">— None —</option>
            {linkedPortcos.map(p => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>STATUS</label>
          <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div />
        <div>
          <label style={labelStyle}>GROSS OPPORTUNITY ($M) *</label>
          <input type="number" step="0.1" value={form.gross_opportunity} onChange={e => setForm(f => ({ ...f, gross_opportunity: e.target.value }))} required placeholder="5.0" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>CONFIDENCE (%) *</label>
          <input type="number" min="0" max="100" step="5" value={form.confidence_pct} onChange={e => setForm(f => ({ ...f, confidence_pct: e.target.value }))} required placeholder="75" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>REALIZATION PERIOD (months)</label>
          <input type="number" min="1" max="120" value={form.realization_months} onChange={e => setForm(f => ({ ...f, realization_months: e.target.value }))} placeholder="18" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>TARGET DATE</label>
          <input type="date" value={form.target_date} onChange={e => setForm(f => ({ ...f, target_date: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>REALIZED VALUE ($M)</label>
          <input type="number" step="0.1" value={form.realized_value} onChange={e => setForm(f => ({ ...f, realized_value: e.target.value }))} placeholder="0" style={inputStyle} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>NOTES</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Rationale, dependencies, assumptions..." style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
      </div>
      {preview && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', marginBottom: 10, display: 'flex', gap: 16, fontSize: 12 }}>
          <TrendingUp size={14} color="#4f46e5" />
          <span>Risk-Adjusted: <strong style={{ color: '#4f46e5' }}>{fmt(preview.riskAdj)}</strong></span>
          <span>Annualized Run-Rate: <strong style={{ color: '#4f46e5' }}>{fmt(preview.annualized)}/yr</strong></span>
          <span style={{ color: '#64748b' }}>(writes to EBITDA Bridge + Base Returns)</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button type="button" className="pi-btn-ghost" onClick={onCancel}>Cancel</button>
        <button type="submit" className="pi-btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Add Synergy'}</button>
      </div>
    </form>
  );
}

function PortcoFinancialContext({ context }) {
  if (!context) return null;
  const { platform, linked_portcos, financials } = context;
  if (!linked_portcos?.length) return null;

  const renderPortco = (p, role) => {
    const fin = financials[p.id];
    return (
      <div key={p.id} className="pi-stat-card" style={{ minWidth: 160 }}>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>
          {role === 'platform' ? 'Platform' : 'Add-on'}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{p.name}</div>
        <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
          <div>
            <div style={{ color: '#94a3b8' }}>EBITDA</div>
            <div style={{ fontWeight: 600 }}>{fmt(fin?.ttm_ebitda || p.entry_ebitda)}</div>
          </div>
          <div>
            <div style={{ color: '#94a3b8' }}>Revenue</div>
            <div style={{ fontWeight: 600 }}>{fmt(fin?.ttm_revenue || p.entry_revenue)}</div>
          </div>
        </div>
        {fin && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Period: {fin.period} ({fin.source})</div>}
      </div>
    );
  };

  return (
    <div className="pi-card" style={{ marginBottom: 16 }}>
      <div className="pi-card-header">
        <span className="pi-card-title"><Link2 size={13} style={{ marginRight: 4 }} />Linked Companies — P&L Context</span>
      </div>
      <div className="pi-card-body" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {renderPortco(platform, 'platform')}
        {linked_portcos.map(p => renderPortco(p, p.role))}
      </div>
    </div>
  );
}

export default function PISynergyTab({ portco, currentUser }) {
  const [synergies, setSynergies] = useState([]);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState({ revenue: false, cost: false });
  const canEdit = hasPI(currentUser, 'pi.synergies.edit');

  const load = useCallback(() => {
    Promise.all([
      piApi.getSynergies(portco.id),
      piApi.getSynergyContext(portco.id),
    ]).then(([synRes, ctxRes]) => {
      setSynergies(synRes.data || []);
      setContext(ctxRes.data || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [portco.id]);

  useEffect(() => { load(); }, [load]);

  const linkedPortcos = context?.linked_portcos || [];

  const revenue = synergies.filter(s => s.type === 'revenue');
  const cost    = synergies.filter(s => s.type === 'cost');

  const totalRevRiskAdj   = revenue.reduce((s, i) => s + (i.risk_adjusted_value || i.modeled_value || 0), 0);
  const totalRevRealized  = revenue.reduce((s, i) => s + (i.realized_value || 0), 0);
  const totalRevRunRate   = revenue.reduce((s, i) => s + (i.annualized_run_rate || 0), 0);
  const totalCostRiskAdj  = cost.reduce((s, i) => s + (i.risk_adjusted_value || i.modeled_value || 0), 0);
  const totalCostRealized = cost.reduce((s, i) => s + (i.realized_value || 0), 0);
  const totalCostRunRate  = cost.reduce((s, i) => s + (i.annualized_run_rate || 0), 0);

  const totalRunRate = totalRevRunRate + totalCostRunRate;

  const SummaryBar = ({ riskAdj, realized, runRate, label }) => {
    const pct = riskAdj ? Math.min(100, Math.round((realized / riskAdj) * 100)) : 0;
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
          <span>{label}</span>
          <span>
            <strong>{fmt(realized)}</strong> / {fmt(riskAdj)} realized ({pct}%)
            {runRate > 0 && <span style={{ marginLeft: 8, color: '#4f46e5' }}>Run-rate: {fmt(runRate)}/yr</span>}
          </span>
        </div>
        <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: '#4f46e5' }} />
        </div>
      </div>
    );
  };

  if (loading) return <div className="pi-loading" style={{ padding: 40 }}><div className="pi-spinner" /><span>Loading synergies...</span></div>;

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Linked companies context */}
      <PortcoFinancialContext context={context} />

      {/* Summary strip */}
      <div className="pi-card" style={{ marginBottom: 20 }}>
        <div className="pi-card-header">
          <span className="pi-card-title">Synergy Summary</span>
          {totalRunRate > 0 && (
            <span style={{ fontSize: 12, color: '#4f46e5', fontWeight: 600 }}>
              Total EBITDA Run-Rate Impact: {fmt(totalRunRate)}/yr
            </span>
          )}
        </div>
        <div className="pi-card-body">
          <SummaryBar riskAdj={totalRevRiskAdj} realized={totalRevRealized} runRate={totalRevRunRate} label="Revenue Synergies" />
          <SummaryBar riskAdj={totalCostRiskAdj} realized={totalCostRealized} runRate={totalCostRunRate} label="Cost Synergies" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {[
          { type: 'revenue', label: 'Revenue Synergies', items: revenue },
          { type: 'cost',    label: 'Cost Synergies',    items: cost },
        ].map(col => (
          <div key={col.type}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: 0 }}>{col.label}</h3>
              {canEdit && (
                <button className="pi-btn-ghost" style={{ fontSize: 11 }} onClick={() => setShowAdd(s => ({ ...s, [col.type]: !s[col.type] }))}>
                  <Plus size={12} /> Add
                </button>
              )}
            </div>
            {showAdd[col.type] && canEdit && (
              <AddSynergyForm portcoId={portco.id} type={col.type} linkedPortcos={linkedPortcos}
                onSaved={() => { setShowAdd(s => ({ ...s, [col.type]: false })); load(); }}
                onCancel={() => setShowAdd(s => ({ ...s, [col.type]: false }))} />
            )}
            {col.items.length === 0 ? (
              <div className="pi-empty" style={{ padding: 24 }}>
                <div className="pi-empty-title">No {col.label.toLowerCase()} yet</div>
                <div className="pi-empty-sub" style={{ fontSize: 11, marginBottom: 8 }}>
                  Add synergies with gross opportunity size, confidence %, and realization timeline.
                </div>
                {canEdit && <button className="pi-btn-ghost" onClick={() => setShowAdd(s => ({ ...s, [col.type]: true }))}><Plus size={13} /> Add first</button>}
              </div>
            ) : col.items.map(s => <SynergyCard key={s.id} synergy={s} canEdit={canEdit} onUpdate={load} linkedPortcos={linkedPortcos} />)
            }
          </div>
        ))}
      </div>
    </div>
  );
}
